import type {ApprovalStepStatus} from './access-request'
import type {ResolverType, ApprovalStrategy} from './approval-policy'
import type {Instant, UUID} from './common'
import type {EntityLink} from './entity-link'
import type {EscalationLevel} from './escalation'
import type {InitialOwnerRequest, OwnerResponse} from './owner'
import type {RuleRequestOwnerResponse} from './auto-assign-rule-request'

/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */
export const AutoAssignRuleType = {
    ACCESS_PROFILE: 'ACCESS_PROFILE',
    FUNCTION_GROUP: 'FUNCTION_GROUP',
} as const;
/* eslint-enable i18next/no-literal-string */
export type AutoAssignRuleType = typeof AutoAssignRuleType[keyof typeof AutoAssignRuleType];

export const AutoAssignWizardStep = {
    BASICS: 0,
    CONDITION: 1,
    TEST: 2,
    PROFILES: 3,
    REVIEW: 4,
} as const
export type AutoAssignWizardStep = typeof AutoAssignWizardStep[keyof typeof AutoAssignWizardStep]

export const AUTO_ASSIGN_WIZARD_STEP_COUNT = 5

export interface AutoAssignRuleFunctionGroupRef {
    id: UUID;
    name: string;
}

export interface AutoAssignRule {
    id: UUID;
    name: string;
    description: string | null;
    conditionExpr: string;
    ruleType: AutoAssignRuleType;
    active: boolean;
    priority: number;
    removalGraceDays: number;
    userRisk: number | null;
    currentVersion: number;
    profileCount: number;
    functionGroups: AutoAssignRuleFunctionGroupRef[];
    owners: OwnerResponse[];
    version: number;
    createdAt: Instant;
    updatedAt: Instant;
}

/**
 * Auto-assign rule version response.
 */
export interface AutoAssignRuleVersion {
    id: UUID;
    ruleId: UUID;
    versionNumber: number;
    name: string;
    description: string | null;
    conditionExpr: string;
    priority: number;
    removalGraceDays: number;
    userRisk: number | null;
    profileIds: UUID[];
    ownerSnapshot: string;
    createdAt: Instant;
    authoredBy: string | null;
}

/**
 * Auto-assign rule profile link response.
 */
export interface AutoAssignRuleProfile {
    id: UUID;
    accessProfile: EntityLink;
    resource: EntityLink;
    system: EntityLink;
}

/**
 * Paginated response for auto-assign rules.
 */
export interface AutoAssignRulePage {
    content: AutoAssignRule[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
}

/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */
export const AutoAssignRuleExecutionStatus = {
    SUCCESS: 'SUCCESS',
    PARTIAL: 'PARTIAL',
    FAILED: 'FAILED',
} as const;
/* eslint-enable i18next/no-literal-string */
export type AutoAssignRuleExecutionStatus = typeof AutoAssignRuleExecutionStatus[keyof typeof AutoAssignRuleExecutionStatus];

/**
 * Auto-assign rule execution log response.
 */
export interface AutoAssignRuleExecutionLog {
    id: UUID;
    ruleId: UUID;
    ruleName: string;
    ruleVersion: number;
    executedAt: Instant;
    durationMs: number;
    status: AutoAssignRuleExecutionStatus;
    identitiesMatched: number;
    assignmentsAdded: number;
    removalsScheduled: number;
}

/**
 * Paginated response for execution logs.
 */
export interface AutoAssignRuleExecutionLogPage {
    content: AutoAssignRuleExecutionLog[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
}

/* eslint-disable i18next/no-literal-string */
export const AutoAssignRuleRequestType = {
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    ACTIVATE: 'ACTIVATE',
    DEACTIVATE: 'DEACTIVATE',
} as const
export type AutoAssignRuleRequestType = typeof AutoAssignRuleRequestType[keyof typeof AutoAssignRuleRequestType]

export const AutoAssignRuleRequestStatus = {
    DRAFT: 'DRAFT',
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
} as const
export type AutoAssignRuleRequestStatus = typeof AutoAssignRuleRequestStatus[keyof typeof AutoAssignRuleRequestStatus]
/* eslint-enable i18next/no-literal-string */

export interface AutoAssignRuleRequest {
    id: UUID
    ruleId: UUID | null
    requestType: AutoAssignRuleRequestType
    status: AutoAssignRuleRequestStatus
    name: string
    description: string | null
    conditionExpr: string
    priority: number
    removalGraceDays: number
    owners: RuleRequestOwnerResponse[]
    userRisk: number | null
    profileIds: UUID[]
    ruleType: AutoAssignRuleType
    functionGroupIds: UUID[]
    reviewedAt: Instant | null
    reviewedBy: EntityLink | null
    reviewComment: string | null
    matchedPolicyName: string | null
    approvalChain: AutoAssignRuleRequestApprovalStep[]
    version: number
    createdAt: Instant
    updatedAt: Instant
}

export interface AutoAssignRuleRequestApprovalStep {
    id: UUID
    stepOrder: number
    resolverType: ResolverType
    resolverParam: string | null
    approvalStrategy: ApprovalStrategy
    required: boolean
    status: ApprovalStepStatus
    activatedAt: Instant | null
    completedAt: Instant | null
    escalationLevel: EscalationLevel
    lastEscalationAt: Instant | null
    approvers: AutoAssignRuleRequestApprover[]
    decisions: AutoAssignRuleRequestApprovalDecision[]
}

export interface AutoAssignRuleRequestApprover {
    id: UUID
    approver: EntityLink
    assignedAt: Instant
}

export interface AutoAssignRuleRequestApprovalDecision {
    id: UUID
    approver: EntityLink
    decision: string
    comment: string | null
    decidedAt: Instant
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const AutoAssignRuleExecutionLogResponseFields = {
    id: 'id',
    ruleId: 'ruleId',
    ruleName: 'ruleName',
    ruleVersion: 'ruleVersion',
    executedAt: 'executedAt',
    durationMs: 'durationMs',
    status: 'status',
    identitiesMatched: 'identitiesMatched',
    assignmentsAdded: 'assignmentsAdded',
    removalsScheduled: 'removalsScheduled',
} as const

export const AutoAssignRuleProfileResponseFields = {
    id: 'id',
    accessProfile: 'accessProfile',
    resource: 'resource',
    system: 'system',
} as const

export const AutoAssignRuleRequestCreateRequestFields = {
    requestType: 'requestType',
    ruleId: 'ruleId',
    name: 'name',
    description: 'description',
    conditionExpr: 'conditionExpr',
    priority: 'priority',
    removalGraceDays: 'removalGraceDays',
    owners: 'owners',
    userRisk: 'userRisk',
    profileIds: 'profileIds',
    ruleType: 'ruleType',
    functionGroupIds: 'functionGroupIds',
} as const

export const AutoAssignRuleRequestResponseFields = {
    id: 'id',
    ruleId: 'ruleId',
    requestType: 'requestType',
    status: 'status',
    name: 'name',
    description: 'description',
    conditionExpr: 'conditionExpr',
    priority: 'priority',
    removalGraceDays: 'removalGraceDays',
    owners: 'owners',
    userRisk: 'userRisk',
    profileIds: 'profileIds',
    ruleType: 'ruleType',
    functionGroupIds: 'functionGroupIds',
    reviewedAt: 'reviewedAt',
    reviewedBy: 'reviewedBy',
    reviewComment: 'reviewComment',
    matchedPolicyName: 'matchedPolicyName',
    approvalChain: 'approvalChain',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const AutoAssignRuleRequestUpdateRequestFields = {
    name: 'name',
    description: 'description',
    conditionExpr: 'conditionExpr',
    priority: 'priority',
    removalGraceDays: 'removalGraceDays',
    owners: 'owners',
    userRisk: 'userRisk',
    profileIds: 'profileIds',
    ruleType: 'ruleType',
    functionGroupIds: 'functionGroupIds',
    version: 'version',
} as const

export const AutoAssignRuleResponseFields = {
    id: 'id',
    name: 'name',
    description: 'description',
    conditionExpr: 'conditionExpr',
    ruleType: 'ruleType',
    active: 'active',
    priority: 'priority',
    removalGraceDays: 'removalGraceDays',
    userRisk: 'userRisk',
    currentVersion: 'currentVersion',
    profileCount: 'profileCount',
    functionGroups: 'functionGroups',
    owners: 'owners',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const AutoAssignRuleVersionResponseFields = {
    id: 'id',
    ruleId: 'ruleId',
    versionNumber: 'versionNumber',
    name: 'name',
    description: 'description',
    conditionExpr: 'conditionExpr',
    priority: 'priority',
    removalGraceDays: 'removalGraceDays',
    userRisk: 'userRisk',
    profileIds: 'profileIds',
    ownerSnapshot: 'ownerSnapshot',
    createdAt: 'createdAt',
    authoredBy: 'authoredBy',
} as const

export const ExpressionSuggestionRequestFields = {
    expression: 'expression',
    cursorPosition: 'cursorPosition',
    schemaTypes: 'schemaTypes',
} as const

export const ExpressionSuggestionResponseFields = {
    suggestions: 'suggestions',
    context: 'context',
} as const

// CPD-OFF — parallel-by-domain with ApprovalStepResponseFields in access-request.ts. Each mirrors a distinct backend approval-step response Record (AutoAssignRuleRequestApprovalStepResponse vs ApprovalStepResponse) that happens to share field names; collapsing them would lose the response-record-to-fields-constant invariant enforced by RequireResponseTypescriptMatch.
export const AutoAssignRuleRequestApprovalStepResponseFields = {
    id: 'id',
    stepOrder: 'stepOrder',
    resolverType: 'resolverType',
    resolverParam: 'resolverParam',
    approvalStrategy: 'approvalStrategy',
    required: 'required',
    status: 'status',
    activatedAt: 'activatedAt',
    completedAt: 'completedAt',
    escalationLevel: 'escalationLevel',
    lastEscalationAt: 'lastEscalationAt',
    approvers: 'approvers',
    decisions: 'decisions',
} as const

export const RuleTestRequestFields = {
    conditionExpr: 'conditionExpr',
    profileIds: 'profileIds',
    limit: 'limit',
    ruleId: 'ruleId',
    rootType: 'rootType',
    rootEntityId: 'rootEntityId',
} as const

export const RuleTestResponseFields = {
    totalEvaluated: 'totalEvaluated',
    matched: 'matched',
    toAdd: 'toAdd',
    toRemove: 'toRemove',
    unchanged: 'unchanged',
    items: 'items',
    testId: 'testId',
} as const

export const ValidateExpressionRequestFields = {
    expression: 'expression',
    rootType: 'rootType',
} as const

export interface AutoAssignRuleRequestCreateRequest {
    requestType: AutoAssignRuleRequestType
    ruleId?: UUID
    name: string
    description?: string
    conditionExpr: string
    priority: number
    removalGraceDays: number
    owners: InitialOwnerRequest[]
    userRisk?: number
    profileIds?: UUID[]
    ruleType?: AutoAssignRuleType
    functionGroupIds?: UUID[]
}

export interface AutoAssignRuleRequestUpdateRequest {
    name: string
    description?: string
    conditionExpr: string
    priority: number
    removalGraceDays: number
    owners: InitialOwnerRequest[]
    userRisk?: number
    profileIds?: UUID[]
    ruleType?: AutoAssignRuleType
    functionGroupIds?: UUID[]
    version: number
}

export const ValidateExpressionResponseFields = {
    valid: 'valid',
    errorMessage: 'errorMessage',
    errorPosition: 'errorPosition',
} as const

export const AutoAssignRuleExecutionFilterParams = {
    ruleId: 'ruleId',
    status: 'status',
} as const

export const ImpactAction = {
    ADD: 'ADD',
    REMOVE: 'REMOVE',
} as const
export type ImpactAction = typeof ImpactAction[keyof typeof ImpactAction]

export interface IdentityFilterCriteria {
    identityTypeCode?: string | null
    lifecycleState?: string | null
}

export interface ImpactAnalysisRequest {
    filter?: IdentityFilterCriteria | null
    maxIdentities?: number | null
}

export interface ImpactItem {
    identityId: UUID
    identityDisplayName: string
    profileId: UUID
    profileDisplayName: string
    action: ImpactAction
    sourceRuleId?: UUID | null
    sourceRuleName?: string | null
}

export interface ImpactAnalysis {
    totalEvaluated: number
    additionCount: number
    removalCount: number
    unchangedCount: number
    truncated: boolean
    items: ImpactItem[]
}

export type ImpactItemResponse = ImpactItem
export type ImpactAnalysisResponse = ImpactAnalysis
/* eslint-enable i18next/no-literal-string */
